import React from 'react';
import {
  Box,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Button,
} from '@material-ui/core';
import SettingsIcon from '@material-ui/icons/Settings';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import RestartAltIcon from '@material-ui/icons/RestartAlt';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

interface ColumnConfigProps {
  columns: Array<{
    id: string;
    label: string;
    isVisible?: boolean;
  }>;
  onColumnToggle: (columnId: string) => void;
  onColumnReorder: (sourceIndex: number, destinationIndex: number) => void;
  onReset: () => void;
}

const ColumnConfig: React.FC<ColumnConfigProps> = ({
  columns,
  onColumnToggle,
  onColumnReorder,
  onReset,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    onColumnReorder(result.source.index, result.destination.index);
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        size="small"
        sx={{ marginLeft: 1, marginRight: 1 }}>
        <SettingsIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: 300,
          },
        }}>
        <Box px={2} py={1} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">Configure Columns</Typography>
          <IconButton
            size="small"
            onClick={() => {
              onReset();
              handleClose();
            }}
            title="Reset to default">
            <RestartAltIcon fontSize="small" />
          </IconButton>
        </Box>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="column-list">
            {(droppableProvided) => (
              <div
                {...droppableProvided.droppableProps}
                ref={droppableProvided.innerRef}>
                {columns.map((column, index) => (
                  <Draggable
                    key={column.id}
                    draggableId={column.id}
                    index={index}>
                    {(draggableProvided, snapshot) => (
                      <MenuItem
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                        dense
                        style={{
                          ...draggableProvided.draggableProps.style,
                          opacity: snapshot.isDragging ? 0.5 : 1,
                          backgroundColor: snapshot.isDragging
                            ? '#f5f5f5'
                            : 'inherit',
                        }}>
                        <div
                          {...draggableProvided.dragHandleProps}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                          }}>
                          <DragIndicatorIcon
                            style={{
                              marginRight: 8,
                              cursor: 'grab',
                              color: '#757575',
                            }}
                          />
                        </div>
                        <Checkbox
                          checked={column.isVisible !== false}
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onColumnToggle(column.id);
                          }}
                        />
                        <Typography variant="body2">{column.label}</Typography>
                      </MenuItem>
                    )}
                  </Draggable>
                ))}
                {droppableProvided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Menu>
    </>
  );
};

export default ColumnConfig;
